import { useEffect, useState, Fragment } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, Activity, FileStack, ShieldCheck, RotateCcw, Infinity as InfinityIcon,
  Trash2, ArrowLeft, Crown, ChevronRight, ChevronDown, Download, TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useAuth } from "@/context/AuthContext";
import { adminStats, adminReset, adminSetUnlimited, adminDeleteUser } from "@/lib/api";

const exportUsersCsv = (users) => {
  const headers = ["Email", "Signed up", "Estimates used", "Free left", "Unlimited", "NDA version", "NDA accepted at", "Retention (days)"];
  const esc = (v) => {
    const s = String(v ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = users.map((u) => [
    u.email,
    u.created_at ? String(u.created_at).slice(0, 10) : "",
    u.credits_used,
    u.unlimited ? "unlimited" : u.remaining,
    u.unlimited ? "yes" : "no",
    u.nda_accepted_at ? (u.nda_accepted_version || "") : "not accepted",
    u.nda_accepted_at || "",
    u.retention_days || 0,
  ]);
  const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `glogrid-usage-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div data-testid={testid} className="rounded-sm border border-border bg-card p-5">
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icon className="h-4 w-4" />
      <span className="font-mono text-[11px] uppercase tracking-widest">{label}</span>
    </div>
    <div className="mt-2 font-display text-4xl font-black tracking-tight">{value}</div>
  </div>
);

export default function Admin() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    try {
      setData(await adminStats());
    } catch (e) {
      if (e?.response?.status === 403 || e?.response?.status === 401) {
        toast.error("Admin access is restricted to the owner.");
        navigate("/");
      }
    }
  };

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_owner) {
      navigate("/");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const act = async (fn, email, okMsg) => {
    setBusy(email);
    try {
      await fn();
      toast.success(okMsg);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed.");
    } finally {
      setBusy("");
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="mx-auto max-w-7xl px-6 py-24 font-mono text-sm text-muted-foreground">Loading admin…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
        <button
          onClick={() => navigate("/")}
          className="mb-4 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:text-primary"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back
        </button>
        <div className="mb-6 flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-primary" />
          <h1 className="font-display text-3xl font-black tracking-tight">Admin — Usage</h1>
        </div>

        <div className="mb-8 grid gap-4 sm:grid-cols-3">
          <Stat icon={Users} label="Signed-in users" value={data.total_users} testid="stat-users" />
          <Stat icon={Activity} label="Users who tried" value={data.users_tried} testid="stat-tried" />
          <Stat icon={FileStack} label="Total estimates" value={data.total_analyses} testid="stat-analyses" />
        </div>

        {/* Signup / estimate trend */}
        <div className="mb-10 rounded-sm border border-border bg-card p-5" data-testid="admin-trend-chart">
          <div className="mb-4 flex items-center gap-2 text-muted-foreground">
            <TrendingUp className="h-4 w-4" />
            <span className="font-mono text-[11px] uppercase tracking-widest">Signups & estimates · last 30 days</span>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={(data.daily_trend || []).map((d) => ({ ...d, label: d.date.slice(5) }))}
                margin={{ top: 5, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fontFamily: "monospace" }}
                  stroke="hsl(var(--muted-foreground))" interval="preserveStartEnd" minTickGap={24} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10, fontFamily: "monospace" }}
                  stroke="hsl(var(--muted-foreground))" />
                <Tooltip
                  contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))",
                    borderRadius: 4, fontSize: 12 }}
                  labelStyle={{ fontFamily: "monospace" }} />
                <Legend wrapperStyle={{ fontSize: 11, fontFamily: "monospace" }} />
                <Line type="monotone" dataKey="signups" name="Signups" stroke="hsl(var(--primary))"
                  strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="estimates" name="Estimates" stroke="hsl(var(--chart-3))"
                  strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Users */}
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-title text-lg font-semibold tracking-tight">Users</h2>
          <Button
            data-testid="export-usage-csv"
            variant="outline"
            size="sm"
            onClick={() => { exportUsersCsv(data.users); toast.success("Usage CSV downloaded."); }}
            className="gap-1.5 rounded-sm font-mono text-xs uppercase tracking-[0.12em]"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </Button>
        </div>
        <div className="mb-10 overflow-x-auto rounded-sm border border-border" data-testid="admin-users-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Signed up</TableHead>
                <TableHead className="text-right">Estimates used</TableHead>
                <TableHead className="text-right">Free left</TableHead>
                <TableHead>NDA accepted</TableHead>
                <TableHead className="text-right">Retention</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.users.map((u) => (
                <Fragment key={u.email}>
                <TableRow data-testid={`admin-user-${u.email}`}>
                  <TableCell className="font-medium">
                    <button
                      onClick={() => setExpanded(expanded === u.email ? null : u.email)}
                      data-testid={`expand-${u.email}`}
                      className="flex items-center gap-1.5 text-left hover:text-primary"
                    >
                      {u.credits_used > 0
                        ? (expanded === u.email ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />)
                        : <span className="w-3.5" />}
                      {u.is_owner && <Crown className="h-3.5 w-3.5 text-chart-3" />}
                      {u.email}
                    </button>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">
                    {u.created_at ? String(u.created_at).slice(0, 10) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono">{u.credits_used}</TableCell>
                  <TableCell className="text-right">
                    {u.unlimited ? (
                      <Badge className="rounded-sm bg-primary/10 text-[10px] uppercase text-primary">Unlimited</Badge>
                    ) : (
                      <span className="font-mono">{u.remaining}</span>
                    )}
                  </TableCell>
                  <TableCell data-testid={`nda-${u.email}`}>
                    {u.nda_accepted_at ? (
                      <span className="font-mono text-xs text-muted-foreground">
                        <Badge className="mr-1.5 rounded-sm bg-emerald-500/10 text-[10px] uppercase text-emerald-600">v{u.nda_accepted_version || "?"}</Badge>
                        {String(u.nda_accepted_at).slice(0, 16).replace("T", " ")}
                      </span>
                    ) : (
                      <Badge className="rounded-sm bg-destructive/10 text-[10px] uppercase text-destructive">Not accepted</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">
                    {u.retention_days ? `${u.retention_days}d` : "—"}
                  </TableCell>
                  <TableCell>
                    {u.is_owner ? (
                      <div className="text-right font-mono text-xs text-muted-foreground">owner</div>
                    ) : (
                      <div className="flex items-center justify-end gap-1.5">
                        <button
                          data-testid={`reset-${u.email}`}
                          disabled={busy === u.email}
                          onClick={() => act(() => adminReset(u.email), u.email, "Free estimates reset.")}
                          title="Reset free estimates"
                          className="flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`unlimited-${u.email}`}
                          disabled={busy === u.email}
                          onClick={() => act(() => adminSetUnlimited(u.email, !u.unlimited), u.email,
                            u.unlimited ? "Unlimited access revoked." : "Unlimited access granted.")}
                          title={u.unlimited ? "Revoke unlimited" : "Grant unlimited"}
                          className={`flex h-8 w-8 items-center justify-center rounded-sm hover:bg-secondary ${u.unlimited ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}
                        >
                          <InfinityIcon className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`delete-${u.email}`}
                          disabled={busy === u.email}
                          onClick={() => {
                            if (window.confirm(`Delete ${u.email}? This removes their account and usage record.`)) {
                              act(() => adminDeleteUser(u.email), u.email, "User removed.");
                            }
                          }}
                          title="Delete user"
                          className="flex h-8 w-8 items-center justify-center rounded-sm text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
                {expanded === u.email && u.credits_used > 0 && (
                  <TableRow className="bg-muted/30">
                    <TableCell colSpan={7} className="p-0">
                      <div className="px-6 py-3" data-testid={`estimates-${u.email}`}>
                        <div className="mb-2 font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                          {u.credits_used} credit{u.credits_used > 1 ? "s" : ""} used · 1 credit per estimate
                        </div>
                        <div className="space-y-1.5">
                          {u.estimates.map((e, i) => (
                            <div key={e.id} className="flex items-center justify-between gap-3 rounded-sm border border-border bg-card px-3 py-2 text-sm">
                              <span className="flex items-center gap-2 truncate">
                                <span className="font-mono text-xs text-muted-foreground">#{u.credits_used - i}</span>
                                <Link to={`/analysis/${e.id}`} className="truncate font-medium hover:text-primary">{e.part_name}</Link>
                              </span>
                              <span className="flex shrink-0 items-center gap-3 font-mono text-xs text-muted-foreground">
                                <span>pin {e.pincode}</span>
                                <span>qty {e.quantity}</span>
                                <span>{e.created_at ? String(e.created_at).slice(0, 16).replace("T", " ") : ""}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Recent estimates */}
        <h2 className="mb-4 font-title text-lg font-semibold tracking-tight">Recent estimates</h2>
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="admin-recent-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Part</TableHead>
                <TableHead>By</TableHead>
                <TableHead>Pincode</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">When</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.recent_analyses.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    <Link to={`/analysis/${r.id}`} className="hover:text-primary">{r.part_name}</Link>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{r.user_email}</TableCell>
                  <TableCell className="font-mono text-xs">{r.pincode}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="rounded-sm text-[10px] uppercase">{r.status}</Badge>
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs text-muted-foreground">
                    {r.created_at ? String(r.created_at).slice(0, 16).replace("T", " ") : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
